import { observer } from 'mobx-react-lite'
import { useEffect, useState, useMemo } from 'react'
import { useHistory, Link, useParams } from 'react-router-dom'
import { TextInput, Textarea, Select, Group, Box, Loader } from '@mantine/core'
import { AppButton, AppText } from '../../app/common/ui'
import { FiZap } from 'react-icons/fi'
import { IoFlash } from 'react-icons/io5'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import LoadingComponent from '../../app/layout/LoadingComponents'
import { buildPath } from '../../app/utils/buildPath'
import { BUILD_PURPOSE_OPTIONS } from './buildPurpose'
import type { BuildPurposeValue } from './buildPurpose'

export default observer(function BuildForm () {
  const { id: editBuildId } = useParams<{ id?: string }>()
  const isEdit = Boolean(editBuildId)
  const { buildStore, firearmModelStore, userStore } = useStore()
  const { createBuild, updateBuild, loadBuild, loading } = buildStore
  const history = useHistory()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [firearmModelId, setFirearmModelId] = useState<string | null>(null)
  const [firearmModelLabel, setFirearmModelLabel] = useState<string | null>(null)
  const [purpose, setPurpose] = useState<BuildPurposeValue | ''>('')
  const [modelOptions, setModelOptions] = useState<{ value: string; label: string }[]>([])
  const [aiFilling, setAiFilling] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [editReady, setEditReady] = useState(!isEdit)

  useEffect(() => {
    firearmModelStore.loadModels().then(() => {
      const approved = firearmModelStore.modelsByDate.filter((m) => m.status === 1)
      setModelOptions(approved.map((m) => ({ value: m.id, label: `${m.make} ${m.model}` })))
    })
  }, [firearmModelStore])

  useEffect(() => {
    if (!isEdit || !editBuildId) {
      setEditReady(true)
      return
    }
    if (!userStore.isLoggedIn) {
      history.replace('/login')
      return
    }
    setEditReady(false)
    let cancelled = false;
    (async () => {
      try {
        const b = await loadBuild(editBuildId)
        if (cancelled || !b) return
        if (userStore.user?.username !== b.ownerUsername) {
          history.replace(`/builds/${editBuildId}`)
          return
        }
        setName(b.name)
        setDescription(b.description ?? '')
        setFirearmModelId(b.firearmModelId)
        setFirearmModelLabel(b.firearmModelName)
        setEditReady(true)
      } catch {
        if (!cancelled) history.push('/builds')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isEdit, editBuildId, loadBuild, history, userStore.isLoggedIn, userStore.user?.username])

  useEffect(() => {
    if (!isEdit && modelOptions.length > 0 && !firearmModelId) {
      setFirearmModelId(modelOptions[0].value)
    }
  }, [isEdit, modelOptions, firearmModelId])

  const selectedModelLabel = useMemo(() => {
    if (isEdit && firearmModelLabel) return firearmModelLabel
    return modelOptions.find((o) => o.value === firearmModelId)?.label
  }, [isEdit, firearmModelLabel, modelOptions, firearmModelId])

  const selectData = useMemo(() => {
    if (!isEdit || !firearmModelId) return modelOptions
    if (modelOptions.some((o) => o.value === firearmModelId)) return modelOptions
    return [...modelOptions, { value: firearmModelId, label: firearmModelLabel ?? 'Selected model' }]
  }, [isEdit, firearmModelId, firearmModelLabel, modelOptions])

  const handleAiFill = async () => {
    if (!name.trim()) return
    setAiFilling(true)
    setAiError(null)
    try {
      const result = await agent.AiAssist.buildFill(name.trim(), selectedModelLabel ?? undefined)
      if (result.description) setDescription(result.description)
    } catch (err: unknown) {
      const is503 = (err as any)?.response?.status === 503
      setAiError(is503 ? 'AI service is currently offline.' : 'AI fill failed. Try again.')
    } finally {
      setAiFilling(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isEdit && editBuildId) {
      if (!name.trim()) return
      try {
        await updateBuild(editBuildId, { name: name.trim(), description: description.trim() || undefined })
        const b = await loadBuild(editBuildId)
        history.push(b ? buildPath(b) : `/builds/${editBuildId}`)
      } catch {
        // interceptor
      }
      return
    }
    if (!firearmModelId) return
    const id = await createBuild({
      name,
      description,
      firearmModelId,
      purpose: purpose || undefined
    })
    if (id) {
      const b = await loadBuild(id)
      history.push(b ? buildPath(b) : `/builds/${id}`)
    }
  }

  if (isEdit && !editReady) {
    return <LoadingComponent content="Loading build…" />
  }

  return (
    <Box sx={{ maxWidth: 600, margin: '0 auto', padding: 24 }}>
      <AppText component={Link} to={isEdit && editBuildId ? `/builds/${editBuildId}` : '/builds'} size="sm" sx={{ color: 'var(--gs-muted)', display: 'block', marginBottom: 20 }}>
        ← {isEdit ? 'Back to build' : 'Back to Builds'}
      </AppText>
      <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 22, marginBottom: 20, color: 'var(--gs-text)' }}>
        {isEdit ? 'EDIT BUILD' : 'CREATE A BUILD'}
      </AppText>
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Build name"
          placeholder="e.g. Precision rifle build"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          mb="md"
        />
        <Select
          label="Firearm model"
          placeholder="Select model"
          data={selectData}
          value={firearmModelId}
          onChange={setFirearmModelId}
          required={!isEdit}
          disabled={isEdit}
          mb="md"
          searchable={!isEdit}
          description={isEdit ? 'Model can’t be changed after the build is created.' : undefined}
        />

        <Select
          label="Build purpose"
          description="Optional — helps others understand what this build is for."
          data={BUILD_PURPOSE_OPTIONS}
          value={purpose}
          onChange={(v) => setPurpose((v ?? '') as BuildPurposeValue | '')}
          clearable
          mb="md"
        />

        {/* Description with AI fill */}
        <Box mb="md">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <AppText size="sm" weight={500}>
              Description{' '}
              <AppText component="span" color="dimmed" size="sm" weight={400}>
                (optional)
              </AppText>
            </AppText>
            <AppButton
              size="xs"
              variant="subtle"
              leftIcon={aiFilling ? <Loader size={11} /> : <FiZap size={11} />}
              disabled={!name.trim() || aiFilling}
              onClick={handleAiFill}
              sx={{
                color: '#E85D04',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 11,
                fontWeight: 700,
                padding: '2px 8px',
                height: 24,
                '&:hover': { background: 'rgba(232,93,4,0.1)' },
                '&[data-disabled]': { color: 'var(--gs-muted)', opacity: 0.5 }
              }}
            >
              {aiFilling ? 'Filling…' : 'AI Fill'}
            </AppButton>
          </Box>
          <Textarea
            placeholder="What this build is for, notes..."
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            minRows={3}
          />
          {aiError && (
            <AppText size="xs" color="red" mt={4}>
              {aiError}
            </AppText>
          )}
          {!name.trim() && (
            <AppText size="xs" color="dimmed" mt={4}>
              Enter a build name above to enable AI fill.
            </AppText>
          )}
        </Box>

        {!isEdit && (
          <Box
            sx={{
              background: 'rgba(201,168,76,0.08)',
              border: '1px solid rgba(201,168,76,0.25)',
              borderRadius: 6,
              padding: '8px 14px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <IoFlash size={13} color="#C9A84C" />
            <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--gs-muted)' }}>
              Submitting a build earns <strong style={{ color: '#C9A84C' }}>AmmoScore</strong> — add parts and photos to earn more.
            </AppText>
          </Box>
        )}

        <Group>
          <AppButton
            type="submit"
            color="red"
            loading={loading}
            disabled={isEdit ? !name.trim() : !firearmModelId || !name.trim()}
          >
            {isEdit ? 'Save changes' : 'Create build'}
          </AppButton>
          <AppButton component={Link} to={isEdit && editBuildId ? `/builds/${editBuildId}` : '/builds'} variant="default">
            Cancel
          </AppButton>
        </Group>
      </form>
    </Box>
  )
})
